/**
 * OfflineIndicator Component
 * Shows a subtle banner when the app is offline or using cached data
 * Automatically appears/disappears based on network status
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, RefreshCw, Cloud, CloudOff, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface OfflineIndicatorProps {
  /** Custom class name */
  className?: string;
  /** Position of the indicator */
  position?: 'top' | 'bottom';
  /** Whether to show when data is from cache */
  showCacheIndicator?: boolean;
}

export function OfflineIndicator({
  className = '',
  position = 'bottom',
  showCacheIndicator = true,
}: OfflineIndicatorProps) {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isUsingCache, setIsUsingCache] = useState(false);

  useEffect(() => {
    const cachedData = localStorage.getItem('YIM_QUERY_CACHE');
    if (cachedData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync state from localStorage on mount
      setIsUsingCache(true);
    }

    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        setShowReconnected(true);
        setDismissed(false);
        // Hide the reconnected message after 3 seconds
        setTimeout(() => {
          setShowReconnected(false);
        }, 3000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      setDismissed(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  // Don't show anything if online and not showing reconnected message
  if (isOnline && !showReconnected && !dismissed) {
    return null;
  }

  // Dismissed state
  if (dismissed && isOnline) {
    return null;
  }

  const positionClasses = position === 'top' 
    ? 'top-0 left-0 right-0' 
    : 'bottom-0 left-0 right-0';

  return (
    <AnimatePresence mode="wait">
      {/* Offline Banner */}
      {!isOnline && !dismissed && (
        <motion.div
          initial={{ y: position === 'top' ? -100 : 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: position === 'top' ? -100 : 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={`fixed ${positionClasses} z-50 ${className}`}
        >
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <WifiOff className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-sm">
                    {t('offline.title', 'Offline Mode')}
                  </p>
                  <p className="text-xs text-white/80">
                    {isUsingCache && showCacheIndicator
                      ? t('offline.usingCache', 'Showing cached data')
                      : t('offline.noConnection', 'No Internet connection')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.location.reload()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  {t('common.tryAgain', 'Try again')}
                </button>
                <button
                  onClick={() => setDismissed(true)}
                  className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                  aria-label={t('common.close')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Reconnected Banner */}
      {showReconnected && isOnline && (
        <motion.div
          initial={{ y: position === 'top' ? -100 : 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: position === 'top' ? -100 : 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={`fixed ${positionClasses} z-50 ${className}`}
        >
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-3 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Cloud className="w-4 h-4" />
              </div>
              <p className="font-medium text-sm">
                {t('offline.reconnected', 'Connection restored!')}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Compact offline indicator for inline use
 */
export function OfflineBadge() {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium"
    >
      <CloudOff className="w-3 h-3" />
      {t('offline.badge', 'Offline')}
    </motion.div>
  );
}

/**
 * Hook to check online status
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook export alongside component
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

export default OfflineIndicator;

